import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { getHead, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, SectionHead, CTABand, FAQBlock } from "../components/Blocks";

const head = getHead("/contact");

const channels = [
  {
    icon: Phone,
    title: "Call Dispatch — 24/7",
    value: PHONE,
    href: PHONE_TEL,
    testid: "contact-phone-card",
    note: "Day or night, weekends and holidays — a live coordinator answers, scopes your needs and starts the process.",
  },
  {
    icon: Mail,
    title: "Email Our Coordination Team",
    value: EMAIL,
    href: EMAIL_MAILTO,
    testid: "contact-email-card",
    note: "Every message is read by our coordination team. Include your city, property type and coverage hours for the fastest scoping.",
  },
];

const checklist = [
  "Property type — construction site, office, retail, residential, event or industrial",
  "City and province where coverage is needed",
  "Hours of coverage — full-time post, overnight, weekends or mobile patrol",
  "Your start date and whether the need is urgent (fire watch, alarm response)",
  "Any current concerns — theft, trespass, vandalism or incident history",
];

const contactFaqs = [
  {
    q: "Is there a contact form on this site?",
    a: "No. We keep it direct: call +1 807 907-2874 or email info@mazesecurity.ca and you reach a real coordinator — no form queues, no ticket numbers.",
  },
  {
    q: "Do you serve my city?",
    a: "We serve all 13 Canadian provinces and territories across more than 190 cities and communities, from Toronto, Montreal and Vancouver to Whitehorse, Yellowknife and Iqaluit. Check our Service Areas page for your city.",
  },
  {
    q: "How quickly can coverage start?",
    a: "Standard engagements begin with a site walk-through and written post orders. Urgent coverage — fire watch, alarm response, strike security — can often be coordinated the same day through our 24/7 dispatch.",
  },
];

export default function Contact() {
  return (
    <>
      <Seo title={head.title} description={head.description} path="/contact" />
      <PageHero
        overline="Contact"
        title="Talk to a Security Coordinator"
        lead="No forms, no ticket queues. One phone line answered 24/7 and one email address read by the people who actually deploy your guards."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="contact-channels">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {channels.map((c) => (
            <a
              key={c.title}
              href={c.href}
              data-testid={c.testid}
              className="group border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(255,85,0,0.15)]"
            >
              <c.icon className="h-8 w-8 text-[#FF5500] mb-4" />
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">{c.title}</h2>
              <p className="mt-2 text-lg font-semibold text-[#FF5500] group-hover:text-white transition-colors">{c.value}</p>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{c.note}</p>
            </a>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          <div className="border border-white/10 bg-[#181C28] rounded-sm p-7" data-testid="contact-coverage-card">
            <MapPin className="h-8 w-8 text-[#FF5500] mb-4" />
            <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">Coverage Area</h2>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              All 13 provinces and territories — Ontario, Quebec, British Columbia, Alberta, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, Newfoundland and Labrador, PEI, Northwest Territories, Yukon and Nunavut — across 690+ cities and communities.
            </p>
          </div>
          <div className="border border-white/10 bg-[#181C28] rounded-sm p-7" data-testid="contact-hours-card">
            <Clock className="h-8 w-8 text-[#FF5500] mb-4" />
            <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">Dispatch Hours</h2>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              24 hours a day, 7 days a week, 365 days a year. Urgent needs — fire watch, alarm response, emergency coverage — are coordinated around the clock.
            </p>
          </div>
        </div>
      </section>
      <section className="bg-[#131620] border-y border-white/10" data-testid="contact-checklist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHead overline="Faster Scoping" title="What to Include When You Reach Out" />
          <ul className="max-w-3xl space-y-3">
            {checklist.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-300 leading-relaxed" data-testid={`checklist-item-${i}`}>
                <span className="text-[#FF5500] font-mono text-xs mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <FAQBlock faqs={contactFaqs} heading="Before You Call — Quick Answers" />
      <CTABand title="Ready to Secure Your Property?" sub="One call or one email reaches the team that deploys your guards. That's the whole process." />
    </>
  );
}
